// Table tile. V-shape-native: resolves each `(column …)`
// `:ref` to a column, zips the columns into row records, and renders
// with the mantine `DataTable`. Synthetic
// `c{i}` accessors avoid collisions when two columns project the same
// underlying name.

import { Badge, Text } from '@mantine/core';
import cx from 'clsx';
import { DataTable, type DataTableColumn, type DataTableSortStatus } from 'mantine-datatable';
import { useId, useState } from 'react';

import {
  formatCompact,
  formatCurrency,
  formatInteger,
  formatNumber,
  formatPercent,
  numberHints,
} from '#core/components/dashboard/number-format.ts';
import type { DisplayFormat } from '#core/lib/expr/ops/dashboard/enums.ts';
import type { TableSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { formatDisplayInstant } from '#core/lib/expr/ops/transform/date-utils.ts';
import type { ColumnType } from '#core/lib/expr/schema/types.ts';
import type { Cell } from '#core/lib/expr/value.ts';
import { cellScalar } from '#core/lib/expr/value.ts';
import { booleanBadgeColor, seriesColorAt, seriesColorFor } from '#core/lib/formatters/colors.ts';
import { csvFromRows } from '#core/lib/transforms/csv-from-rows.ts';
import { slugify } from '#core/lib/utils/slugify.ts';

import { ladderedUnitDisplay, unitLabel } from '../unit-label.ts';
import { compareScalar, renderCell } from './cell.ts';
import classes from './TablePanel.module.css';
import { TileFrame } from './TileFrame.tsx';
import { useResolvedColumns } from './useResolvedColumns.ts';

type Row = Record<string, number | string | null>;

/** Stable color name for a category value — delegates to the shared
 * `seriesColorFor` hash so the same value always renders the same color,
 * independent of row order or the distinct-value set. */
const categoryColor = seriesColorFor;

const PAGE_SIZE = 50;

export function TablePanel({ spec }: { spec: TableSpec }) {
  const tileId = useId();
  const cols = useResolvedColumns(spec.columns.map((c) => c.ref));
  const rowCount = cols.reduce((max, col) => Math.max(max, col.length), 0);

  // `_id` is a synthetic, row-index-based key consumed by mantine-datatable
  // via `idAccessor` below. The source row index is the only value guaranteed
  // unique per record AND stable across sort/pagination — column values are
  // not (the first column may legitimately repeat: status, category, etc.),
  // and post-sort array positions would shift on every reorder.
  const records: Row[] = Array.from({ length: rowCount }, (_, r) => ({
    _id: r,
    ...Object.fromEntries(cols.map((col, i) => [`c${i}`, cellScalar(col[r])])),
  }));

  // Initial sort: first column with an explicit `:sort`; else the
  // first `datetime` column (desc); else the first column ascending.
  const explicitIndex = spec.columns.findIndex((c) => c.sort !== undefined);
  const datetimeIndex = spec.columns.findIndex((c) => c.format === 'datetime');
  const resolveInitialSort = (): DataTableSortStatus<Row> => {
    if (explicitIndex >= 0) {
      return { columnAccessor: `c${explicitIndex}`, direction: spec.columns[explicitIndex]?.sort ?? 'asc' };
    }
    if (datetimeIndex >= 0) {
      return { columnAccessor: `c${datetimeIndex}`, direction: 'desc' };
    }
    return { columnAccessor: 'c0', direction: 'asc' };
  };
  const initialStatus: DataTableSortStatus<Row> = resolveInitialSort();

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Row>>(initialStatus);

  // Controlled sorting — mantine-datatable does NOT sort `records`
  // itself; derive the sorted array ourselves from `sortStatus`.
  const sortedRecords = records.toSorted((ra, rb) => {
    const av = ra[String(sortStatus.columnAccessor)] ?? null;
    const bv = rb[String(sortStatus.columnAccessor)] ?? null;
    const cmp = compareScalar(av, bv);
    return sortStatus.direction === 'desc' ? -cmp : cmp;
  });

  const [page, setPage] = useState(1);
  const from = (page - 1) * PAGE_SIZE;
  const to = from + PAGE_SIZE;
  const slicedRecords = sortedRecords.slice(from, to);
  const pageProps =
    records.length > PAGE_SIZE * 1.25
      ? {
          onPageChange: setPage,
          page: page,
          recordsPerPage: PAGE_SIZE,
          totalRecords: records.length,
        }
      : {};

  // CSV view: project synthetic `c{i}` accessors onto user-facing column
  // labels. Sorted records win — what the user sees in the table is what
  // lands in the CSV. The pagination slice is intentionally bypassed:
  // the download spans the full dataset, not the current page window.
  const getCsv = () =>
    csvFromRows(
      sortedRecords,
      spec.columns.map((c, i) => ({ source: `c${i}`, label: c.label })),
    );
  const tileName = `table-${slugify(spec.columns.at(0)?.label ?? 'rows')}`;

  return (
    <TileFrame getCsv={getCsv} id={tileId} inspect={() => ({ spec, records })} name={tileName}>
      <div className={classes.table}>
        <DataTable<Row>
          columns={spec.columns.map((c, i) => resolveColumn<Row>(c, i, sortStatus, cols[i] ?? []))}
          defaultColumnProps={{ noWrap: true }}
          idAccessor="_id"
          minHeight={records.length > 0 ? undefined : 150}
          onSortStatusChange={setSortStatus}
          pinFirstColumn={spec.columns.length > 1}
          records={slicedRecords}
          sortStatus={sortStatus}
          withTableBorder={false}
          {...pageProps}
        />
      </div>
    </TileFrame>
  );
}

function resolveColumn<T extends Row>(
  colSpec: TableSpec['columns'][number],
  index: number,
  sortStatus: DataTableSortStatus<T>,
  values: readonly Cell[],
): DataTableColumn<T> {
  const accessor = `c${index}`;
  const isActiveSort = sortStatus.columnAccessor === accessor;
  // The referenced column's engine type drives enum-aware rendering: enum
  // columns default to a category badge and color by declared member order.
  const colType = colSpec.ref.ds.schema.get(colSpec.ref.column);

  // Number precision is inferred ONCE over the full column, not per
  // cell — same mechanism CartesianPanel uses for axis/tooltip ticks.
  const numericValues = values.filter((v): v is number => typeof v === 'number');
  const columnNumberHints = numberHints(numericValues);
  // Laddered numeric columns fix ONE rung per column from the column's group
  // max, fold magnitude into the petroleum unit prefix (header carries the
  // laddered unit), and render each cell as the bare scaled mantissa. Undefined
  // for unit-less / fixed-symbol dimensions — those keep `columnNumberHints`.
  const laddered = ladderedUnitDisplay(colType, numericValues);
  // When a column ladders, the petroleum prefix on the header carries the
  // magnitude, so EVERY numeric format renders the bare scaled mantissa — a
  // `compact`/`integer` cell must not re-apply an SI `K`/`M` on top of the
  // laddered unit. Only unit-less columns keep their own numeric formatter.
  const numberFmt = laddered ? laddered.format : (value: number) => formatNumber(value, columnNumberHints);
  const compactFmt = laddered ? laddered.format : formatCompact;
  const integerFmt = laddered ? laddered.format : formatInteger;

  const columnSpecs: Partial<Record<DisplayFormat, Partial<DataTableColumn<T>>>> = {
    datetime: {
      title: colSpec.label ?? 'Date/Time',
      textAlign: 'right',
      render: (record) => renderCell(record, accessor, (value) => formatDisplayInstant(value) ?? String(value)),
    },
    sparkline: {
      render: (record) =>
        renderCell(record, accessor, (_value) => {
          return 'Sparkline (not implemented)';
        }),
    },
    text: {
      render: (record) =>
        renderCell(record, accessor, (value) => {
          if (Array.isArray(value)) {
            if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
              return (value as (string | number)[]).map((v) => (
                <Badge color={categoryColor(v.toString())} key={v} variant="outline">
                  {v}
                </Badge>
              ));
            }
            const validValues = (value as object[]).filter((v) => v !== null && v !== undefined);
            if (!validValues.length) {
              return 'n/a';
              // return renderCell(record, accessor, (value) => null);
            }
            return validValues
              .map((v) => JSON.stringify(v))
              .map((v) => (
                <Badge color={categoryColor(v)} key={v} variant="transparent">
                  {v}
                </Badge>
              ));
          }

          if (typeof value === 'object') {
            if ('externalId' in value) {
              return normalizeId((value as { externalId: string }).externalId);
            }
            return <span>{JSON.stringify(value)}</span>;
          }

          if (
            typeof value === 'string' &&
            value.includes(':') &&
            ['string', 'well', 'asset', 'company', 'reservoir', 'field', 'location'].includes(
              value.split(':').at(0) ?? '',
            )
          ) {
            return normalizeId(value);
          }
          return String(value);
        }),
    },
    number: {
      textAlign: 'right',
      render: (record) => renderCell(record, accessor, (value) => numberFmt(+value)),
    },
    integer: {
      textAlign: 'right',
      render: (record) =>
        typeof record[accessor] === 'number' ? integerFmt(record[accessor] as number) : String(record[accessor]),
    },
    compact: {
      textAlign: 'right',
      render: (record) =>
        typeof record[accessor] === 'number' ? compactFmt(record[accessor] as number) : String(record[accessor]),
    },
    percent: {
      textAlign: 'right',
      render: (record) =>
        typeof record[accessor] === 'number' ? formatPercent(record[accessor] as number) : String(record[accessor]),
    },
    currency: {
      textAlign: 'right',
      render: (record) =>
        typeof record[accessor] === 'number' ? formatCurrency(record[accessor] as number) : String(record[accessor]),
    },
    boolean: {
      textAlign: 'center',
      render: (record) =>
        renderCell(record, accessor, (value) => {
          // `cellScalar` stringifies booleans, so a true cell arrives as
          // 'true'; also accept numeric/string 1 for tolerant sources.
          const truthy = value === 'true' || value === 1 || value === '1';
          return (
            <Badge color={booleanBadgeColor(truthy)} variant="light">
              {truthy ? 'True' : 'False'}
            </Badge>
          );
        }),
    },
    category: {
      render: (record) =>
        renderCell(record, accessor, (value) => {
          const label = String(value);
          // Enum columns color by declared member order (stable, palette-spread);
          // other category columns fall back to the per-value hash.
          const memberIndex = colType?.kind === 'enum' ? colType.members.indexOf(label) : -1;
          const color = memberIndex >= 0 ? seriesColorAt(memberIndex) : categoryColor(label);
          if (!label) {
            return (
              <Badge color="dark.4" variant="outline">
                not set
              </Badge>
            );
          }
          return (
            <Badge color={color} variant="light">
              {label}
            </Badge>
          );
        }),
    },
  } as const;

  const sortable = true; // colSpec.sortable ?? colSpec.sort !== undefined;

  // Default format by engine column kind: enum → category badge, temporal
  // (timestamp/date) → datetime (so epoch-ms instants render as dates via
  // `formatDateValue`, never as raw integers), everything else → text. A
  // lookup keeps the kind→format mapping declarative; the spec's explicit
  // `format` (below) always wins over this default.
  const defaultFormatByKind: Partial<Record<ColumnType['kind'], DisplayFormat>> = {
    enum: 'category',
    timestamp: 'datetime',
    date: 'datetime',
  };
  const defaultFormat: DisplayFormat = (colType?.kind && defaultFormatByKind[colType.kind]) ?? 'text';

  // Declared unit rides alongside the header label as a dim suffix (numeric
  // leaves only — `unitLabel` is blank for unit-less/dimensionless columns).
  const baseTitle = colSpec.label ?? `Column ${index + 1}`;
  const unitText = laddered?.unit ?? unitLabel(colType);
  const title = unitText ? (
    <>
      {baseTitle}{' '}
      <Text c="dimmed" component="span" size="sm">
        {unitText}
      </Text>
    </>
  ) : (
    baseTitle
  );

  return {
    accessor,
    title,
    sortable,
    cellsClassName: isActiveSort ? classes.sortedColumn : undefined,
    titleClassName: isActiveSort ? cx(classes.sortedColumn) : undefined,
    ...columnSpecs[colSpec.format ?? defaultFormat],
  };
}

function normalizeId(id: string): string {
  if (id.includes(':')) {
    return id.split(':').slice(1).join(':');
  }
  return id;
}
